import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Paperclip, Send, X, ImageIcon } from "lucide-react";

/**
 * Threaded notes on an invoice with optional image attachments.
 * Visible & writable by any user whose company is either the sender or receiver
 * (backend enforces this — same check as invoice detail access).
 */
export default function InvoiceNotes({ invoiceId, currentUser }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/notes`);
      setNotes(data);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  // Fetch attachment blobs as they appear
  useEffect(() => {
    const toFetch = notes
      .filter((n) => n.attachment && !attachmentUrls[n.attachment.id])
      .map((n) => n.attachment);
    if (toFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const att of toFetch) {
        try {
          const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
          updates[att.id] = URL.createObjectURL(res.data);
        } catch (e) {
          // Attachment fetch failed — surface in console so devs can debug, but don't
          // break the whole notes view (other attachments should still render).
          console.warn(`Failed to load attachment ${att.id}:`, e?.message || e);
        }
      }
      if (!cancelled) setAttachmentUrls((prev) => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [notes, attachmentUrls]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    const urls = attachmentUrls;
    return () => {
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [attachmentUrls]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|gif|webp)$/.test(f.type)) {
      setError("Only jpg, png, gif, or webp images are supported.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError("File too large (max 8 MB).");
      return;
    }
    setError("");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && !file) return;
    setSubmitting(true);
    setError("");
    try {
      let attachmentId = null;
      if (file) {
        const form = new FormData();
        form.append("file", file);
        const uploadResp = await api.post(`/attachments?invoice_id=${invoiceId}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        attachmentId = uploadResp.data.id;
      }
      await api.post(`/invoices/${invoiceId}/notes`, {
        body: body.trim() || "(image attached)",
        attachment_id: attachmentId,
      });
      setBody("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to post note");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card-flat" data-testid="invoice-notes-card">
      <div className="px-5 py-4 border-b border-neutral-900">
        <div className="label-mini">conversation</div>
        <h3 className="font-display text-xl font-semibold">Notes</h3>
        <p className="text-xs text-neutral-500 mt-1">
          Visible to both vendor and customer companies on this invoice.
        </p>
      </div>

      <div className="px-5 py-4 space-y-4 max-h-96 overflow-y-auto" data-testid="invoice-notes-list">
        {loading && <div className="text-xs text-neutral-500">loading…</div>}
        {!loading && notes.length === 0 && (
          <div className="text-xs text-neutral-500 italic">
            No notes yet. Start the conversation below.
          </div>
        )}
        {notes.map((n) => {
          const mine = currentUser && n.author_id === currentUser.id;
          return (
            <div
              key={n.id}
              className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
              data-testid={`note-${n.id}`}
            >
              <div className="label-mini">
                {n.author_name}
                {n.author_company_name && <> · <span className="text-neutral-900">{n.author_company_name}</span></>}
                {" · "}{formatDateTime(n.created_at)}
              </div>
              <div
                className={`mt-1 max-w-md border px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  mine ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white"
                }`}
              >
                {n.body}
              </div>
              {n.attachment && (
                <div className="mt-2 max-w-md">
                  {attachmentUrls[n.attachment.id] ? (
                    <a href={attachmentUrls[n.attachment.id]} target="_blank" rel="noreferrer">
                      <img
                        src={attachmentUrls[n.attachment.id]}
                        alt={n.attachment.filename}
                        className="max-h-56 border border-neutral-200 object-contain"
                        data-testid={`attachment-image-${n.attachment.id}`}
                      />
                    </a>
                  ) : (
                    <div className="shimmer h-32 w-48 border border-neutral-200" />
                  )}
                  <div className="label-mini mt-1 flex items-center gap-1">
                    <ImageIcon size={10} /> {n.attachment.filename}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <form className="px-5 py-4 border-t border-neutral-200 space-y-3" onSubmit={submit} data-testid="note-form">
        <textarea
          className="input-field resize-none"
          rows={3}
          placeholder="write a note about this invoice — visible to both vendor and customer…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="note-body-input"
        />
        {file && (
          <div className="flex items-center gap-2 text-xs">
            <ImageIcon size={12} />
            <span className="truncate max-w-xs">{file.name}</span>
            <button
              type="button"
              onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              className="text-neutral-500 hover:text-neutral-900"
              data-testid="note-attachment-remove"
            >
              <X size={12} />
            </button>
          </div>
        )}
        {error && (
          <div className="border border-red-600 text-red-600 text-xs px-3 py-2" data-testid="note-error">{error}</div>
        )}
        <div className="flex items-center justify-between gap-2">
          <label className="btn-outline flex items-center gap-2 cursor-pointer" data-testid="note-attach-button">
            <Paperclip size={12} /> attach image
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={onPickFile}
              data-testid="note-file-input"
            />
          </label>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={submitting || (!body.trim() && !file)}
            data-testid="note-submit-button"
          >
            <Send size={12} />
            {submitting ? "posting…" : "post note"}
          </button>
        </div>
      </form>
    </div>
  );
}
